import { useEffect, useMemo, useState } from "react";
import { list, remove } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import EventSelect from "@/components/EventSelect";
import { StatusBadge } from "@/components/StatusBadge";
import { ResourceForm } from "@/components/ResourcePage";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { PAYMENT_STATUSES, fmtIDR, fmtDate, daysUntil } from "@/lib/constants";
import { useEventOptions } from "@/hooks/useEventOptions";
import { useAuth } from "@/context/AuthContext";
import { Plus, Pencil, Trash2, AlertTriangle, Send, Stamp } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import api from "@/lib/api";

const Stat = ({ label, value, color }) => (
  <div className="card-lux p-5">
    <p className="text-xs uppercase tracking-wider text-[#7A7A7A]">{label}</p>
    <p className={cn("font-heading text-2xl mt-1.5 leading-none", color)}>{value}</p>
  </div>
);

const effStatus = (p) => {
  const d = daysUntil(p.due_date);
  if (p.status !== "PAID" && d != null && d < 0) return "OVERDUE";
  return p.status;
};

export default function Payments() {
  const { can } = useAuth();
  const { events } = useEventOptions();
  const [eventId, setEventId] = useState("all");
  const [payments, setPayments] = useState([]);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = () => {
    const params = eventId !== "all" ? { event_id: eventId } : {};
    list("payments", params).then(setPayments).catch(() => setPayments([]));
  };
  useEffect(load, [eventId]); // eslint-disable-line

  const stats = useMemo(() => {
    const relEvents = eventId === "all" ? events : events.filter((e) => e.id === eventId);
    const contract = relEvents.reduce((s, e) => s + (Number(e.total_price) || 0), 0);
    const paid = payments.filter((p) => p.status === "PAID").reduce((s, p) => s + (Number(p.amount) || 0), 0);
    const overdue = payments.filter((p) => effStatus(p) === "OVERDUE").reduce((s, p) => s + (Number(p.amount) || 0), 0);
    return { contract, paid, outstanding: Math.max(contract - paid, 0), overdue };
  }, [payments, events, eventId]);

  const del = async (id) => { await remove("payments", id); toast.success("Dihapus"); load(); };
  const remind = async (id) => {
    try { const r = await api.get(`/reminders/payment/${id}/link`); window.open(r.data.wa_link, "_blank"); toast.success("Membuka WhatsApp..."); }
    catch (e) { toast.error(e.response?.data?.detail || "Gagal membuka WhatsApp"); }
  };
  const eventName = (id) => { const e = events.find((x) => x.id === id); return e ? `${e.groom} & ${e.bride}` : "-"; };
  const makeReceipt = async (id) => {
    try { const r = await api.post(`/receipts/generate/${id}`); toast.success(`Kuitansi ${r.data.receipt_number} diterbitkan (lihat menu Kuitansi)`); }
    catch (e) { toast.error(e.response?.data?.detail || "Gagal membuat kuitansi"); }
  };

  const fields = [
    { name: "label", label: "Nama Pembayaran", required: true, full: true },
    { name: "event_id", label: "Event", type: "select", options: events.map((e) => ({ value: e.id, label: `${e.groom} & ${e.bride}` })), full: true },
    { name: "amount", label: "Jumlah", type: "number", required: true },
    { name: "status", label: "Status", type: "select", options: PAYMENT_STATUSES, default: "UNPAID" },
    { name: "due_date", label: "Jatuh Tempo", type: "date" },
    { name: "paid_date", label: "Tanggal Dibayar", type: "date" },
    { name: "notes", label: "Catatan", type: "textarea", full: true },
  ];

  return (
    <div>
      <PageHeader overline="Keuangan Client" title="Payments" subtitle="Kelola termin pembayaran dan pantau outstanding.">
        {can(3) && <Button onClick={() => { setEditing(null); setFormOpen(true); }} data-testid="payments-add-btn" className="bg-burgundy hover:bg-burgundy-dark text-white rounded-full"><Plus className="w-4 h-4 mr-1" />Pembayaran</Button>}
      </PageHeader>

      <div className="mb-4"><EventSelect value={eventId} onChange={setEventId} /></div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5 stagger">
        <Stat label="Total Contract" value={fmtIDR(stats.contract)} color="text-[#1A1A1A]" />
        <Stat label="Total Paid" value={fmtIDR(stats.paid)} color="text-emerald-600" />
        <Stat label="Outstanding" value={fmtIDR(stats.outstanding)} color="text-amber-600" />
        <Stat label="Overdue" value={fmtIDR(stats.overdue)} color="text-red-600" />
      </div>

      <div className="card-lux overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-beige/60 hover:bg-beige/60 border-gold/20">
                {["Pembayaran", "Event", "Jumlah", "Jatuh Tempo", "Status", "Aksi"].map((h) => <TableHead key={h} className="text-[11px] uppercase tracking-wider text-[#4A4A4A]">{h}</TableHead>)}
              </TableRow>
            </TableHeader>
            <TableBody>
              {payments.length === 0 ? (
                <TableRow><TableCell colSpan={6} className="text-center py-10 text-[#7A7A7A]">Belum ada pembayaran.</TableCell></TableRow>
              ) : payments.map((p) => {
                const d = daysUntil(p.due_date);
                const soon = p.status !== "PAID" && d != null && d >= 0 && d <= 7;
                return (
                  <TableRow key={p.id} className="border-gold/10 hover:bg-beige/30" data-testid="payment-row">
                    <TableCell className="text-sm font-medium text-[#1A1A1A]">{p.label}</TableCell>
                    <TableCell className="text-sm text-[#4A4A4A]">{eventName(p.event_id)}</TableCell>
                    <TableCell className="text-sm font-semibold text-[#1A1A1A]">{fmtIDR(p.amount)}</TableCell>
                    <TableCell className="text-sm text-[#4A4A4A]">
                      <span className="flex items-center gap-1">{fmtDate(p.due_date)}{soon && <span className="text-amber-600 flex items-center gap-0.5 text-[10px] font-semibold"><AlertTriangle className="w-3 h-3" />{d}h</span>}</span>
                    </TableCell>
                    <TableCell><StatusBadge status={effStatus(p)} /></TableCell>
                    <TableCell className="whitespace-nowrap">
                      {can(2) && p.status !== "PAID" && <button onClick={() => remind(p.id)} title="Kirim reminder WhatsApp" data-testid="payment-remind-wa" className="p-1.5 text-emerald-600 hover:text-emerald-700"><Send className="w-4 h-4" /></button>}
                      {can(4) && <button onClick={() => makeReceipt(p.id)} title="Buat Kuitansi" data-testid="payment-make-receipt" className="p-1.5 text-burgundy hover:text-burgundy-dark"><Stamp className="w-4 h-4" /></button>}
                      {can(3) && <>
                        <button onClick={() => { setEditing(p); setFormOpen(true); }} className="p-1.5 text-[#7A7A7A] hover:text-burgundy" data-testid="payment-edit"><Pencil className="w-4 h-4" /></button>
                        <button onClick={() => del(p.id)} className="p-1.5 text-[#7A7A7A] hover:text-red-600" data-testid="payment-delete"><Trash2 className="w-4 h-4" /></button>
                      </>}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </div>

      <ResourceForm open={formOpen} onOpenChange={setFormOpen} fields={fields} initial={editing} resource="payments" onSaved={load} defaults={eventId !== "all" ? { event_id: eventId } : {}} title={editing ? "Edit Pembayaran" : "Tambah Pembayaran"} />
    </div>
  );
}
